import React, { useCallback } from 'react';
import cx from 'classnames';

import {
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  SettingsManager,
  useApplicationState,
  useBetslipState,
  useBetslipStateUpdate,
  useSettings,
} from '@atlas/core';
import { ToggleCheckbox } from '@atlas/ui';

import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import { CANNOT_TURN_OFF_QUICK_BET_ERROR } from './constants';
import { useLocalBetslip } from './LocalBetslipContext';

const BetslipSwitchers = () => {
  const { account, authenticated, bankAccounts, resolved } = useApplicationState();
  const { betItems, loading, settings } = useBetslipState();
  const { quickBet: isQuickBet } = useSettings();
  const { isOrderBet, setIsOpenPopup, setIsOrderBet } = useLocalBetslip();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const betslipStateUpdate = useBetslipStateUpdate();

  const handleToggleQuickBet = useCallback(async () => {
    if (
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.BET_SLIP)
    ) {
      setIsOpenPopup(false);
      return NotificationManager.display({
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    if (loading) {
      return betslipStateUpdate({ errors: CANNOT_TURN_OFF_QUICK_BET_ERROR });
    }
    SettingsManager.updateSettings({ quickBet: !isQuickBet });
  }, [
    account,
    bankAccounts,
    showSpentLimitPopupIfNeeded,
    showSelfRegulationPopupIfNeeded,
    loading,
    isQuickBet,
    setIsOpenPopup,
    betslipStateUpdate,
  ]);

  const handleToggleOrderBet = useCallback(async () => {
    if (
      needsVerification(account, bankAccounts) &&
      isUserActionNeedPhotoVerification(account, PhotoVerificationAction.BET_SLIP)
    ) {
      setIsOpenPopup(false);
      return NotificationManager.display({
        message: 'verificationPopup',
        type: 'verificationPopup',
      });
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    setIsOrderBet(!isOrderBet);
  }, [
    account,
    bankAccounts,
    isOrderBet,
    setIsOpenPopup,
    setIsOrderBet,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopupIfNeeded,
  ]);

  const toggleQuickBetClass = cx('toggle-container', { active: isQuickBet });
  const toggleOrderBetClass = cx('toggle-container', { active: isOrderBet });

  // Betslip switchers visible only for authorized users
  if (resolved && authenticated) {
    // Checking all rules for order bet switch
    const isOrderBetAllowed = !isQuickBet && !!settings.orderBet && betItems.length > 0;

    return (
      <div className="quick-bet-container">
        <div className={toggleQuickBetClass}>
          <ToggleCheckbox
            checked={isQuickBet}
            disabled={loading || isOrderBet}
            id="betslip-quick-bet"
            label="turn_quick_bet_on"
            onChange={handleToggleQuickBet}
          />
        </div>

        {isOrderBetAllowed && (
          <div className={toggleOrderBetClass}>
            <ToggleCheckbox
              checked={isOrderBet}
              disabled={loading}
              id="betslip-order-bet"
              label="turn_order_bet_on"
              onChange={handleToggleOrderBet}
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default BetslipSwitchers;
